// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { DonutChartContainer } from '@shared/ui-kit/components';
import { classNames } from '@shared/ui-kit/utils';
import { defineComponent } from '@shared/ui-kit/utils/define-component';
import { type DebtBreakdownSlice } from '@shared/advisory/aggregation/utils/build-debt-breakdown';
import {
  type DebtBreakdownView,
  useDebtBreakdownChart,
} from './use-debt-breakdown-chart';

const DebtBreakdownViewToggle = ({
  viewMode,
  onViewModeChange,
  bankLabel,
  typeLabel,
}: {
  viewMode: DebtBreakdownView;
  onViewModeChange: (viewMode: DebtBreakdownView) => void;
  bankLabel: string;
  typeLabel: string;
}) => (
  <div
    className="mx-auto inline-flex rounded-full bg-border-sep/40 p-1"
    data-testid="debt-breakdown-view-toggle"
    role="group"
    aria-label={bankLabel}
  >
    <button
      type="button"
      className={classNames('rounded-full px-4 py-1.5 text-sm transition-colors', {
        'bg-white font-medium text-primary-medium shadow-sm': viewMode === 'bank',
        'text-control-track': viewMode !== 'bank',
      })}
      onClick={() => onViewModeChange('bank')}
      data-testid="debt-breakdown-view-bank"
      aria-pressed={viewMode === 'bank'}
    >
      {bankLabel}
    </button>

    <button
      type="button"
      className={classNames('rounded-full px-4 py-1.5 text-sm transition-colors', {
        'bg-white font-medium text-primary-medium shadow-sm': viewMode === 'type',
        'text-control-track': viewMode !== 'type',
      })}
      onClick={() => onViewModeChange('type')}
      data-testid="debt-breakdown-view-type"
      aria-pressed={viewMode === 'type'}
    >
      {typeLabel}
    </button>
  </div>
);

export const DebtBreakdownChart = defineComponent({
  displayName: 'DebtBreakdownChart',
  useSetup: ({
    breakdownByBank,
    breakdownByType,
  }: {
    breakdownByBank: DebtBreakdownSlice;
    breakdownByType: DebtBreakdownSlice;
  }) => useDebtBreakdownChart({ breakdownByBank, breakdownByType }),
  View: ({ t, viewMode, setViewMode, title, exposure, weights }) => (
    <DonutChartContainer
      name={title}
      exposure={exposure}
      weight={weights}
      testId="debt-breakdown-donut-chart"
      divider={false}
      compactLayout
      footer={
        <DebtBreakdownViewToggle
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          bankLabel={t('aggregator.loans.view-by-bank', { ns: 'aggregator' })}
          typeLabel={t('aggregator.loans.view-by-type', { ns: 'aggregator' })}
        />
      }
    />
  ),
});
